import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Status badge — soft tinted background with a darker text colour of the same
 * hue. Fully rounded.
 */
export const pillVariants = cva('inline-flex items-center gap-1 rounded-full font-semibold whitespace-nowrap', {
  variants: {
    color: {
      positive: 'bg-positive-subtle text-positive-text',
      negative: 'bg-negative-subtle text-negative-text',
      info: 'bg-info-subtle text-info-text',
      warning: 'bg-warning-subtle text-warning-text',
      /** Brand-tinted; alias of `warning` for in-progress / attention states. */
      primary: 'bg-warning-subtle text-warning-text',
      neutral: 'bg-neutral-subtle text-fg-muted',
      default: 'bg-neutral-subtle text-fg-secondary',
      muted: 'bg-neutral-subtle text-fg-muted',
    },
    size: {
      sm: 'px-2 py-[3px] text-2xs leading-[14px]',
      md: 'px-2.5 py-1 text-xs',
    },
  },
  defaultVariants: {
    color: 'default',
    size: 'sm',
  },
});

export type PillColor = NonNullable<VariantProps<typeof pillVariants>['color']>;

export interface PillProps extends VariantProps<typeof pillVariants> {
  children: React.ReactNode;
  className?: string;
  /** Tooltip — for a pill whose text is a shorthand, like `1/1` or "3 uur geleden". */
  title?: string;
}

export const Pill: React.FC<PillProps> = ({ children, color, size, className, title }) => {
  return (
    <span className={pillVariants({ color, size, className })} title={title}>
      {children}
    </span>
  );
};
